import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, QueryList, ViewChildren, ChangeDetectionStrategy, inject } from '@angular/core';
import { Order } from '../../interfaces/orders/order';
import { Asset } from '../../interfaces/assets/asset';
import { SelectOption } from '../../interfaces/select-option';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { eAssetType, assetTypeLookup, eOrderStatusType } from '../../enums';
import { map, Observable, of, Subject, takeUntil, timer } from 'rxjs';
import { ValidationProblem } from '../../interfaces/validation/validation-problem';
import { EmptyEntityService } from '../../services/empty-entity-service/empty-entity.service';
import { AssetUIConfiguration } from '../../interfaces/ui-configurations/asset-ui-configuration';
import { HandleAssetComponent } from '../handle-asset/handle-asset.component';
import { ToastrService } from 'ngx-toastr';
import { OrderManagerService } from '../../services/order-manager/order-manager.service';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { EnumeratedTypesRepositoryService } from '../../services/enumerated-types/enumerated-types-repository.service';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { AssetRepositoryService } from '../../services/asset/asset-repository.service';
import { PatchDocument } from '../../interfaces/patch-document';
import { TranslocoService } from '@jsverse/transloco';

@Component({
    selector: 'lib-asset-list',
    templateUrl: './asset-list.component.html',
    styleUrls: ['./asset-list.component.css'],
    animations: [
        trigger('detailExpand', [
            state('collapsed,void', style({ height: '0px', minHeight: '0' })),
            state('expanded', style({ height: '*' })),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AssetListComponent implements OnInit, OnDestroy {
  private readonly transloco = inject(TranslocoService);
  @ViewChildren(HandleAssetComponent) handleAssetComponents!: QueryList<HandleAssetComponent>;

  @Input() order!: Order;
  @Input() observableAssets$!: Observable<Asset[]>;
  @Input() uiConfiguration!: AssetUIConfiguration;

  @Input() isEditable: boolean = false;
  @Input() errors$!: Observable<ValidationProblem[] | undefined>;

  @Output() assetAddedEvent = new EventEmitter<Asset>();
  @Output() assetsUploadedEvent = new EventEmitter<Asset[]>();
  @Output() allAssetsRemovedEvent = new EventEmitter<void>();
  @Output() assetUpdatedEvent = new EventEmitter<Asset>();
  @Output() assetCopiedEvent = new EventEmitter<Asset>();
  @Output() assetRemovedEvent = new EventEmitter<Asset>();
  @Output() assetFormValueChangedEvent = new EventEmitter<Asset>();
  @Output() assetSearchRequestedEvent = new EventEmitter<string>();

  @Input() expandedAsset: Asset | null = null;

  displayedColumns: string[] = ['num', 'serial-number', 'asset-type', 'year', 'make', 'model'];
  isLoading: boolean = false;

  private assets: Asset[] = [];
  private onDestroy$ = new Subject<void>();
  protected assetTypes: SelectOption[] = [];

  constructor(
    private emptyEntityService: EmptyEntityService,
    private messageService: ToastrService,
    private orderManager: OrderManagerService,
    private dialog: MatDialog,
    private enumeratedTypesRepository: EnumeratedTypesRepositoryService,
    private assetRepository: AssetRepositoryService) { }

  ngOnInit(): void {
    if (!this.observableAssets$) {
      this.observableAssets$ = of(this.order.assets ?? []);
    }

    this.observableAssets$ = this.observableAssets$
      .pipe(map(assets => assets
        .filter(a => a.movedToGeneralCollateral === false)
        .sort((a, b) => a.priority - b.priority)));

    this.handlePossibleColumnChanges();

    this.observableAssets$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(assets => this.assets = assets);

    this.orderManager.savingAsset$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(val => this.isLoading = val);

    this.enumeratedTypesRepository.getAssetTypesAsSelectOptions(this.order.orderTypeID, this.order.jurisdiction, this.order.orderStatusTypeID === eOrderStatusType.historicalDraft)
      .subscribe(types => this.assetTypes = types);
  }

  onSaving(): Asset | null {
    if (this.expandedAsset) {
      const expandedComponent = this.handleAssetComponents.find(component => component.asset === this.expandedAsset);

      if (expandedComponent) {
        return expandedComponent.getAsset();;
      }
    }

    return null;
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  onEditButtonClick = (assetToEdit: Asset) => {
    if (this.isEditable) {
      this.expandedAsset = this.expandedAsset === assetToEdit ? null : assetToEdit;
    }
  }

  onDeleteButtonClick = (event: Event, assetToDelete: Asset) => {
    // emit this asset to delete
    this.assetRemovedEvent.emit(assetToDelete);
  }

  onCopyButtonClick = (event: Event, assetToCopy: Asset) => {
    const priority = this.getNewPriority();
    const copiedAsset = { ...assetToCopy, priority };
    copiedAsset.shouldTriggerPostRegistrationSearch = false;
    this.assetCopiedEvent.emit(copiedAsset);
  }

  onUndoButtonClick = (event: Event, assetToUndo: Asset, rowIndex: number) => {

    // emit this party to delete
    //this.partyRemovedEvent.emit(partyToUndo);

    // filter out deleted party from list of displayed parties
    //this.parties = this.parties.filter((u) => u.id !== partyToUndo.id);
    //this.order.assets = this.assets;
  }

  handleAssetUpdateEvent(asset: Asset) {
    const index = this.order.assets!.findIndex(a => a.id === asset.id);
    if (index !== -1) {
      asset.shouldTriggerPostRegistrationSearch = this.order.assets![index].shouldTriggerPostRegistrationSearch;
      this.order.assets![index] = asset;
    }

    this.assetUpdatedEvent.emit(asset);

    this.expandedAsset = null;

    this.handlePossibleColumnChanges();
  }

  handleAssetRemovedEvent(asset: Asset) {
    this.assetRemovedEvent.emit(asset);

    this.expandedAsset = null;

    this.handlePossibleColumnChanges();
  }

  onClearAssets() {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.transloco.translate('assetList.removeAllAssetsTitle'),
        confirmationPrompt: this.transloco.translate('common.labels.actionNotReversible'),
        positiveResponseLabel: this.transloco.translate('common.buttons.yes'),
        negativeResponseLabel: this.transloco.translate('common.buttons.cancel')
      },
      width: '600px'
    });

    dialogRef.afterClosed().subscribe(res => {
      if (res) {
        this.allAssetsRemovedEvent.emit();
      }
    })
  }

  handleAssetFormValueChangedEvent(asset: Asset) {
    this.assetFormValueChangedEvent.emit(asset);
  }

  protected addNewAsset(): void {
    const priority = this.getNewPriority();
    this.assetAddedEvent.emit(this.emptyEntityService.getEmptyAssetWithType(eAssetType.None, this.order.id ?? '', priority ?? 0))
  }

  getNumber(asset: Asset) {
    const index = this.assets.indexOf(asset);

    if (index === -1) {
      return 0;
    }

    return index + 1;
  }

  getAssetTypeString(asset: Asset) {
    const assetTypeID = assetTypeLookup[asset.assetTypeID] ?? asset.assetTypeID;

    switch (assetTypeID) {
      case eAssetType.MotorVehicle:
        return this.transloco.translate('enums.assetType.MotorVehicle');
      case eAssetType.Trailer:
        return this.transloco.translate('enums.assetType.Trailer');
      case eAssetType.Boat:
        return this.transloco.translate('enums.assetType.Boat');
      case eAssetType.MobileHome:
        return this.transloco.translate('enums.assetType.MobileHome');
      case eAssetType.OutboardMotor:
        return this.transloco.translate('enums.assetType.OutboardMotor');
      case eAssetType.AircraftRegisteredInCanada:
        return this.transloco.translate('enums.assetType.AircraftRegisteredInCanada');
      case eAssetType.AircraftNonRegisteredInCanada:
        return this.transloco.translate('enums.assetType.AircraftNonRegisteredInCanada');
      case eAssetType.ManufacturedHome:
        return this.transloco.translate('enums.assetType.ManufacturedHome');
      case eAssetType.FarmVehicle:
        return this.transloco.translate('enums.assetType.FarmVehicle');
      case eAssetType.Motorcycle:
        return this.transloco.translate('enums.assetType.Motorcycle');
      case eAssetType.Bus:
        return this.transloco.translate('enums.assetType.Bus');
      case eAssetType.MiniBus:
        return this.transloco.translate('enums.assetType.MiniBus');
      case eAssetType.Taxi:
        return this.transloco.translate('enums.assetType.Taxi');
      case eAssetType.AllTerrainVehicle:
        return this.transloco.translate('enums.assetType.AllTerrainVehicle');
      case eAssetType.SnowmobilePost1988:
        return this.transloco.translate('enums.assetType.SnowmobilePost1988');
      case eAssetType.PassengerVehicle:
        return this.transloco.translate('enums.assetType.PassengerVehicle');
      case eAssetType.EmergencyVehicle:
        return this.transloco.translate('enums.assetType.EmergencyVehicle');
      case eAssetType.CommercialVehicle:
        return this.transloco.translate('enums.assetType.CommercialVehicle');
      case eAssetType.Other:
        return this.transloco.translate('common.labels.other');
      case eAssetType.AircraftCanada:
        return this.transloco.translate('enums.assetType.AircraftCanada');
      case eAssetType.AircraftForeign:
        return this.transloco.translate('enums.assetType.AircraftForeign');
      case eAssetType.AircraftAirframeRegisteredInCanada:
        return this.transloco.translate('enums.assetType.AircraftAirframeRegisteredInCanada');
      case eAssetType.AircraftAirframeNotRegisteredInCanada:
        return this.transloco.translate('enums.assetType.AircraftAirframeNotRegisteredInCanada');
      case eAssetType.Aircraft:
        return this.transloco.translate('enums.assetType.Aircraft');
      case eAssetType.AircraftDOT:
        return this.transloco.translate('enums.assetType.AircraftDOT');
      case eAssetType.AircraftSerial:
        return this.transloco.translate('enums.assetType.AircraftSerial');
      case eAssetType.TrailerOrSemiTrailer:
        return this.transloco.translate('enums.assetType.TrailerOrSemiTrailer');
      case eAssetType.MotorHome:
        return this.transloco.translate('enums.assetType.MotorHome');
      default:
        return "";
    }
  }

  handlePossibleColumnChanges() {
    const hasManufacturedHomeRegistrationNumber = this.order.assets?.some(asset => asset.manufacturedHomeRegistrationNumber) ?? false;
    if (hasManufacturedHomeRegistrationNumber && this.uiConfiguration.showManufacturedHomeRegistrationNumber) {
      this.displayedColumns = ['num', 'serial-number', 'asset-type', 'manufactured-home-registration-number', 'make', 'model', 'year'];
    } else {
      this.displayedColumns = ['num', 'serial-number', 'asset-type', 'make', 'model', 'year'];
    }

    if (this.uiConfiguration.showColor) {
      this.displayedColumns = this.displayedColumns.concat(['colour']);
    }

    if (this.uiConfiguration.showPostRegistrationSearchCheckbox) {
      this.displayedColumns = this.displayedColumns.concat(['post-search']);
    }

    if (this.isEditable) {
      this.displayedColumns = this.displayedColumns.concat(['actions']);
    }
  }

  private getNewPriority() {
    //Want the highest priority + 1
    let priority = this.assets
      .map(a => a.priority)
      .sort((a, b) => b - a)
      .at(0);

    if (priority === undefined || priority === 0) {
      priority = 1;
    } else {
      priority++;
    }

    return priority;
  }

  handleAssetSearchRequestedEvent(serialNumber: string) {
    this.assetSearchRequestedEvent.emit(serialNumber);
  }

  onAssetsUpload(event: Event) {
    const element = event.currentTarget as HTMLInputElement;
    let file = element.files ? element.files[0] : null;

    if (file) {
      const reader = new FileReader();
      const lowerCaseAssetTypes = Object.values(eAssetType).map(t => t.toString().toLowerCase().replaceAll(' ', ''));

      reader.onload = () => {
        let lines = reader.result?.toString().split('\n').map(n => n.trim());
        const firstLine = lines?.at(0);

        if (file.type === 'text/csv' ||
          file.type === 'application/csv' ||
          file.type === 'application/vnd.ms-excel') {
          lines = lines?.slice(1, lines.length).filter(line => !!line);
        } else {
          this.messageService.error(this.transloco.translate('assetList.invalidFileType'));
          return
        }

        if (!firstLine ||
          lines?.length === 0) {
          this.messageService.warning(this.transloco.translate('assetList.noAssetsInFile'));

          return;
        }

        const splitFirstLine = firstLine.split(',').map(val => val.toLowerCase());
        const typeIndex = splitFirstLine.findIndex(val => val.toLowerCase().includes('type'));
        const makeIndex = splitFirstLine.findIndex(val => val.toLowerCase() === 'make');
        const modelIndex = splitFirstLine.findIndex(val => val.toLowerCase() === 'model');
        const yearIndex = splitFirstLine.findIndex(val => {
          val = val.toLowerCase();

          return val === 'year' || val === 'yyyy';
        });
        const colourIndex = splitFirstLine.findIndex(val => val.toLowerCase().startsWith('colo'));
        const numberIndex = splitFirstLine.findIndex(val => {
          val = val.toLowerCase();

          return val === 'vin' || val.includes('serial') || val.includes('number');
        });

        const assets = lines?.map(line => {
          var asset: any = {};
          var splitLine = line.split(',');

          if (typeIndex !== -1) {
            let type = splitLine.at(typeIndex)?.replaceAll(' ', '').toLowerCase();

            if (type === 'mv') {
              type = 'motorvehicle';
            }

            if (lowerCaseAssetTypes.find(t => t === type)) {
              asset['assetTypeID'] = type;
            }
          }

          if (makeIndex !== -1) {
            asset['make'] = splitLine.at(makeIndex);
          }

          if (modelIndex !== -1) {
            asset['model'] = splitLine.at(modelIndex);
          }

          if (yearIndex !== -1) {
            asset['year'] = splitLine.at(yearIndex);
          }

          if (colourIndex !== -1) {
            asset['color'] = splitLine.at(colourIndex);
          }

          if (numberIndex !== -1) {
            asset['serialNumber'] = splitLine.at(numberIndex);
          }

          return asset;
        });

        this.assetsUploadedEvent.emit(assets ?? []);
      }

      reader.readAsText(file);
    }
  }

  onDownloadTemplate() {
    const blob = new Blob(['assetType,serialNumber,make,model,year,colour'], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    
    const a = document.createElement('a');
    a.href = url;
    a.download = 'Asset_Information_Sample.csv'; 
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  onPostRegSearchCheckboxClick(asset: Asset, event: MatCheckboxChange) {
    asset.shouldTriggerPostRegistrationSearch = event.checked;
    const patchDocument: PatchDocument[] = [ { op: 'replace', value: asset.shouldTriggerPostRegistrationSearch, path: 'shouldTriggerPostRegistrationSearch' } ];

    this.assetRepository.patchAsset(this.order.id, asset.id, patchDocument).subscribe((a: Asset) => {
      const index = this.order.assets!.findIndex(obj => obj.id === a.id);

      if (index != -1) {
        this.order.assets![index] = a;
        this.orderManager.updateOrder(this.order);
      }
    });
  }
}
